import React, { useState, useEffect, useCallback } from "react";
import { useProfile } from "../contexts/ProfileContext";
import { supabase } from "../supabaseClient";

function ManageOwners({ onClose }) {
  const { selectedProfile: profile, fetchProfiles } = useProfile();
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newOwnerEmail, setNewOwnerEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [confirmRemoval, setConfirmRemoval] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  // Fetch owners
  const fetchOwners = useCallback(async () => {
    if (!profile?.profile_id) return;

    setLoading(true);
    try {
      // Fetch current owners
      const { data: currentOwners, error: ownersError } = await supabase
        .from("user_profiles")
        .select("email")
        .eq("profile_id", profile.profile_id);

      if (ownersError) {
        throw new Error(ownersError.message || "Failed to fetch owners");
      }

      // Fetch pending invites
      const { data: pendingInvites, error: invitesError } = await supabase
        .from("user_profiles_invites")
        .select("email")
        .eq("profile_id", profile.profile_id);

      if (invitesError) {
        throw new Error(invitesError.message || "Failed to fetch invites");
      }

      // Combine and mark the type
      const allOwners = [
        ...(currentOwners || []).map((owner) => ({ ...owner, type: "owner" })),
        ...(pendingInvites || []).map((invite) => ({
          ...invite,
          type: "pending",
        })),
      ];

      setOwners(allOwners);
      setError("");
    } catch (err) {
      setError(err.message);
      console.error("[Error] Failed to fetch owners:", err);
    } finally {
      setLoading(false);
    }
  }, [profile?.profile_id]);

  // Fetch data when component mounts
  useEffect(() => {
    fetchOwners();
  }, [fetchOwners]);

  // Add owner
  const handleAddOwner = async (e) => {
    e.preventDefault();
    if (!newOwnerEmail.trim() || !profile?.profile_id) return;

    setIsSubmitting(true);
    setError("");
    setSuccessMessage("");

    try {
      // First check if the email is already an owner
      const { data: existingOwner, error: checkError } = await supabase
        .from("user_profiles")
        .select("email")
        .eq("profile_id", profile.profile_id)
        .eq("email", newOwnerEmail)
        .maybeSingle();

      if (checkError) {
        throw new Error(checkError.message);
      }

      if (existingOwner) {
        throw new Error("This email is already an owner of this profile");
      }

      // Check if there's a pending invite
      const { data: existingInvite, error: inviteCheckError } = await supabase
        .from("user_profiles_invites")
        .select("email")
        .eq("profile_id", profile.profile_id)
        .eq("email", newOwnerEmail)
        .maybeSingle();

      if (inviteCheckError) {
        throw new Error(inviteCheckError.message);
      }

      if (existingInvite) {
        throw new Error("An invite has already been sent to this email");
      }

      // Get current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      // Add to user_profiles_invites
      const { error: inviteError } = await supabase
        .from("user_profiles_invites")
        .insert({
          profile_id: profile.profile_id,
          email: newOwnerEmail,
          user_id: user.id,
        });

      if (inviteError) {
        throw new Error(inviteError.message || "Failed to add owner");
      }

      // Refresh data
      await fetchOwners();
      await fetchProfiles();
      setNewOwnerEmail("");
      setSuccessMessage("Invite sent successfully!");
    } catch (err) {
      setError(err.message);
      console.error("[Error] Failed to add owner:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Remove owner or cancel invite
  const handleRemoveOwner = async (email) => {
    if (!profile?.profile_id) return;

    setIsSubmitting(true);
    setError("");

    try {
      const owner = owners.find((o) => o.email === email);
      if (!owner) {
        throw new Error("Owner not found");
      }

      // Get current session to include the auth token
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();

      if (sessionError) {
        throw new Error(sessionError?.message || "Failed to get session");
      }

      if (!session?.access_token) {
        throw new Error("No authentication token available");
      }

      // Call the edge function to remove the owner/invite
      const { data, error: edgeFunctionError } =
        await supabase.functions.invoke("remove_profile_owner", {
          body: {
            profileId: profile.profile_id,
            email: email,
            type: owner.type,
          },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

      if (edgeFunctionError) {
        throw new Error(
          edgeFunctionError?.message || "Failed to remove owner/invite"
        );
      }

      if (!data?.success) {
        throw new Error(data?.error || "Failed to remove owner/invite");
      }

      // Refresh data
      await fetchOwners();
      await fetchProfiles();
      setConfirmRemoval(null);
      setSuccessMessage(data.message);
    } catch (err) {
      setError(err.message);
      console.error("[Error] Failed to remove owner/invite:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!profile?.profile_id) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-base-200 rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Add/Remove Parents/Guardians</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="alert alert-info mb-4">
          <span>All owners will receive Progress Report emails</span>
        </div>

        {error && (
          <div className="alert alert-error mb-4">
            <p>{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success mb-4">
            <p>{successMessage}</p>
          </div>
        )}

        {/* Add new owner form */}
        <form onSubmit={handleAddOwner} className="mb-6">
          <div className="form-control">
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="Enter email address"
                className="input input-bordered flex-1"
                value={newOwnerEmail}
                onChange={(e) => setNewOwnerEmail(e.target.value)}
                disabled={isSubmitting}
                required
              />
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Adding..." : "Add"}
              </button>
            </div>
          </div>
        </form>

        {/* Owners list */}
        {loading ? (
          <div className="flex justify-center py-4">
            <span className="loading loading-spinner loading-md"></span>
          </div>
        ) : (
          <ul className="divide-y divide-base-300">
            {owners.map((owner) => (
              <li
                key={owner.email}
                className="py-2 flex justify-between items-center"
              >
                <div className="flex items-center gap-2">
                  <span>{owner.email}</span>
                  {owner.type === "pending" && (
                    <span className="badge badge-warning badge-sm">
                      Pending
                    </span>
                  )}
                </div>
                {owner.type === "owner" &&
                  owners.filter((o) => o.type === "owner").length > 1 &&
                  (confirmRemoval === owner.email ? (
                    <div className="flex gap-2">
                      <button
                        className="btn btn-sm btn-error"
                        onClick={() => handleRemoveOwner(owner.email)}
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Removing..." : "Confirm"}
                      </button>
                      <button
                        className="btn btn-sm"
                        onClick={() => setConfirmRemoval(null)}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="btn btn-sm btn-error"
                      onClick={() => setConfirmRemoval(owner.email)}
                      disabled={isSubmitting}
                    >
                      Remove
                    </button>
                  ))}
                {owner.type === "pending" && (
                  <button
                    className="btn btn-sm btn-error"
                    onClick={() => handleRemoveOwner(owner.email)}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Canceling..." : "Cancel"}
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default ManageOwners;
